/**
 * Style-example rows, stored as columns so they can be translated.
 *
 * Two widgets show a small table of citation examples — a style name, a list label, the
 * formatted entry, and its in-text form. The natural shape is an array of row objects,
 * and that is how both were written. But `ToolUiValue` holds only strings, string
 * arrays and keyed maps of them on purpose: a translator's file may not smuggle a
 * number, a boolean or an array of objects into a slot the widget renders as text. An
 * array of objects is not expressible in it, so the rows were silently untranslatable —
 * and the first translator to try got a load-time error rather than a page.
 *
 * Widening the value type for two widgets was the wrong trade. The same objection that
 * kept a one-page block type out of the content model applies here: every shape added is
 * a shape every future translation and every future validator has to carry. Columns cost
 * nothing the model does not already support.
 *
 * Per-column rather than per-row fallback is the one thing given up, and it is the right
 * thing to give up: a translator who renders the style names and leaves the worked
 * citations in English is doing exactly what the three-tier rule asks, and that is a
 * whole column either way.
 */

export interface ExampleColumns {
  style: string[];
  label: string[];
  example: string[];
  inText: string[];
}

export interface ExampleRow {
  style: string;
  label: string;
  example: string;
  inText: string;
}

/**
 * Zip columns into rows, falling back to English per cell.
 *
 * English decides how many rows there are. A translation that supplies a short column —
 * three style names against four examples — fills the gap from English instead of
 * rendering `undefined`, because a missing label is a worse outcome than an untranslated
 * one and neither should be a blank.
 */
export function exampleRows(
  english: ExampleColumns,
  translated: ExampleColumns
): ExampleRow[] {
  return english.example.map((_, index) => ({
    style: translated.style[index] ?? english.style[index],
    label: translated.label[index] ?? english.label[index],
    example: translated.example[index] ?? english.example[index],
    inText: translated.inText[index] ?? english.inText[index],
  }));
}

/** A table stored as columns: field name to one string per row. */
export type Columns<K extends string> = Record<K, string[]>;

/**
 * Zip any set of columns into rows, falling back to English per cell.
 *
 * The general form of `exampleRows`, for example lists whose fields are not the four
 * style-table ones (a debate contention, a DBQ document, a reference). English decides
 * the row count — the longest English column — so a translation with a short column
 * fills the gap from English, and a missing or empty column degrades to a fully English
 * row rather than `undefined`. Fields that are not text (a group number, a sourcing
 * token) do not belong in a column at all; index a module-level constant by row.
 */
export function zipColumns<K extends string>(
  english: Columns<K>,
  translated: Columns<K>
): Record<K, string>[] {
  const keys = Object.keys(english) as K[];
  const count = Math.max(0, ...keys.map((key) => english[key].length));
  return Array.from({ length: count }, (_, index) => {
    const row = {} as Record<K, string>;
    for (const key of keys) {
      row[key] = translated[key]?.[index] ?? english[key][index] ?? "";
    }
    return row;
  });
}
